import { useBoundContext } from "lib/@components/binding/use-bound-context"
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    List,
    ListSubheader,
    Stack,
    Typography,
    useTheme,
} from "@mui/material"
import { FilterLabel } from "routes/asset-register/filter-label"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useMemo } from "react"
import { DebouncedTextField } from "lib/@components/debounced-text-field"
import { matches } from "library/matches.mjs"
import { setFromEvent } from "lib/setFromEvent"
import { uniqueValues } from "routes/asset-register/controller/uniqueValues"
import { VirtualWindow } from "lib/@components/virtual-window/VirtualWindow"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import Iconify from "minimals-template/components/Iconify"
import { AssetOpenFiltersButton } from "./plugins/filtering/asset-open-filters-button"

const empty = {}

export function FilterList({ isFilterVisible, handleClick }) {
    const theme = useTheme()
    const { target, setFilters } = useBoundContext()
    const [search, setSearch] = useSearchParam("asset-filters-search", "")
    const lists = uniqueValues.useResults(target.id) ?? empty
    const output = useMemo(() => {
        const facilities = lists.facilities?.filter((s) => matches(s, search))
        const floors = lists.floors?.filter((s) => matches(s, search))
        const systems = lists.systems?.filter((s) => matches(s, search))
        const categories = lists.categories?.filter((s) => matches(s, search))
        const manufacturers = lists.manufacturers?.filter((s) => matches(s, search))
        const spaces = lists.spaces?.filter((s) => matches(s, search))
        const types = lists.types?.filter((s) => matches(s, search))
        const output = []
        addOutput("facilities", facilities)
        addOutput("floors", floors)
        addOutput("systems", systems)
        addOutput("categories", categories)
        addOutput("types", types)
        addOutput("spaces", spaces)
        addOutput("manufacturers", manufacturers)
        return output

        function addOutput(name, list) {
            if (list?.length) {
                output.push(
                    <ListSubheader key={name}>
                        <Typography variant="overline">{name.titleize()}</Typography>
                    </ListSubheader>
                )
                output.push(...list.map((f) => <FilterLabel key={`${name}_${f}`} item={f} type={name} />))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, lists])

    return (
        <>
            <ListItemBox spacing={0.5} sx={{ display: "block" }}>
                <Stack spacing={2} sx={{ justifyContent: "center" }}>
                    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle1" sx={{ paddingLeft: 2 }}>
                            Filters
                        </Typography>
                        <AssetOpenFiltersButton handleClick={handleClick} isOpen={isFilterVisible} />
                    </Stack>
                    <Stack spacing={2} sx={{ justifyContent: "center" }}>
                        <DebouncedTextField
                            data-cy="register-filter-list-search-box"
                            sx={{ "& *": { fontSize: 14 } }}
                            value={search}
                            onChange={setFromEvent(setSearch)}
                            placeholder="Search..."
                            variant="outlined"
                            size="small"
                            aria-label="Search bar for filters"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton aria-label="Search">
                                            <Iconify icon="iconamoon:search" width="24px" height="24px" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack direction="row">
                            <Button
                                startIcon={<Iconify icon="ic:round-clear" size={12} />}
                                onClick={() => setFilters({})}
                                data-cy="assets-clear-filter-btn"
                            >
                                Clear Filters
                            </Button>
                            <Box flex={1} />
                        </Stack>
                    </Stack>
                </Stack>
            </ListItemBox>
            <FitInRemainingSpace
                setHeight
                Component={List}
                leaveSpace={30}
                dense
                sx={{
                    py: 0,
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiListSubheader-root": {
                        top: 0,
                        my: 0,
                        py: 0,
                        bgcolor: theme.palette.background.neutral,
                    },
                }}
            >
                <VirtualWindow key={search}>{output}</VirtualWindow>
            </FitInRemainingSpace>
        </>
    )
}
